// ─── Chin Bot Global Restore ──────────────────────────────
// Auto-starts Chin bot engines after login without needing to
// open the ChinMonitor page. Engines survive tab switches,
// minimize, and page navigation. Only stop on explicit off
// or browser close.
//
// Each Spotware account has its own independent session stored
// under a composite key: {strategyId}__account_{accountId}.
//
// Architecture mirrors useTrailingGlobalRestore for consistency.

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getOrCreateChinEngine,
  getChinEngine,
  getAllChinEngines,
} from '@/lib/chin-bot-engine';
import {
  applyGlobalWorkspace,
  abortAllGlobalEngines,
  clearAllGlobalRiskEvaluations,
} from '@/lib/chin-global-workspace';
import type { WorkspaceSymbolConfig } from '@/lib/chin-global-workspace';
import { buildSessionPrefix, parseSessionKey } from '@/lib/session-key';

// ─── Types ──────────────────────────────────────────────────

interface ChinStrategyRow {
  id: string;
  name: string;
  active_accounts: string[];
  symbol_list_id: string | null;
  chinparams: Record<string, unknown> | null;
}

interface SymbolEntry {
  symbolId: number;
  symbolName: string;
  digits?: number;
}

interface SymbolListRow {
  id: string;
  name: string;
  ctrader_account_id: string | null;
  symbols: SymbolEntry[];
}

interface ChinSessionRow {
  strategy_id: string;
  session_data: Record<string, unknown>;
}

const CHIN_TIMEFRAME = 'H1';

// ─── Hook ───────────────────────────────────────────────────

export function useChinGlobalRestore(userId: string | null) {
  const restoredRef = useRef(false);
  const pendingTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const retryCountRef = useRef(0);

  // ─── Helper: clear all pending timers ────────────────────
  const clearAllTimers = () => {
    pendingTimersRef.current.forEach((t) => clearTimeout(t));
    pendingTimersRef.current = [];
  };

  useEffect(() => {
    console.log(`[ChinGlobalRestore] 🔌 Effect triggered — userId="${userId}" roleLoading=${!userId}`);

    // ─── Logout: stop all chin engines ─────────────────────
    if (!userId) {
      console.log('[ChinGlobalRestore] 🛑 No userId — cleaning up all engines');
      const engines = getAllChinEngines();
      for (const [key, engine] of engines) {
        const parsed = parseSessionKey(key);
        if (parsed) {
          abortAllGlobalEngines(parsed.strategyId, parsed.accountId);
        }
        engine.stop();
      }
      clearAllGlobalRiskEvaluations();
      restoredRef.current = false;
      retryCountRef.current = 0;
      clearAllTimers();
      return;
    }

    // ─── Already restored successfully? Skip ───────────────
    if (restoredRef.current) {
      console.log('[ChinGlobalRestore] ✅ Already restored, skipping');
      return;
    }

    console.log('[ChinGlobalRestore] 🚀 Starting restore sequence...');

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 4000, 8000, 16000, 30000];

    const scheduleTimer = (fn: () => void, delayMs: number) => {
      const t = setTimeout(fn, delayMs);
      pendingTimersRef.current.push(t);
      return t;
    };

    // ─── Query all sessions for a strategy (composite + legacy keys) ──

    const querySessionsForStrategy = async (
      stratId: string,
    ): Promise<Map<string, Record<string, unknown>>> => {
      const accountSessions = new Map<string, Record<string, unknown>>();

      try {
        // Query composite keys: {strategyId}__account_%
        const prefix = buildSessionPrefix(stratId);
        const { data: compositeRows, error: compositeErr } = await supabase
          .from('bot_session_state')
          .select('strategy_id, session_data')
          .eq('user_id', userId)
          .like('strategy_id', prefix);

        if (!compositeErr && compositeRows) {
          for (const row of (compositeRows as unknown as ChinSessionRow[])) {
            const parsed = parseSessionKey(row.strategy_id);
            if (parsed && row.session_data?.type === 'chin') {
              accountSessions.set(parsed.accountId, row.session_data);
            }
          }
        }

        // Fallback: try legacy key (just strategyId, no account suffix)
        if (accountSessions.size === 0) {
          const { data: legacyRow, error: legacyErr } = await supabase
            .from('bot_session_state')
            .select('session_data')
            .eq('user_id', userId)
            .eq('strategy_id', stratId)
            .maybeSingle();

          if (!legacyErr && legacyRow?.session_data && legacyRow.session_data.type === 'chin') {
            // Legacy session — account is stored in session_data.selectedAccountId
            const legacyAccount = (legacyRow.session_data.selectedAccountId as string) || '';
            if (legacyAccount) {
              accountSessions.set(legacyAccount, legacyRow.session_data);
              console.log(`[ChinGlobalRestore] 📦 Using legacy session for strategy "${stratId}" account=${legacyAccount}`);
            }
          }
        }
      } catch (err) {
        console.warn('[ChinGlobalRestore] Session query error:', err);
      }

      return accountSessions;
    };

    const tryRestore = async (attempt: number) => {
      console.log(`[ChinGlobalRestore] 🔄 tryRestore attempt=${attempt}/${MAX_RETRIES - 1}`);

      try {
        // Step 1: Fetch all chin type strategies for this user
        console.log('[ChinGlobalRestore] 📡 Fetching chin strategies...');
        const { data: strategies, error } = await supabase
          .from('strategies')
          .select('id, name, active_accounts, symbol_list_id, chinparams')
          .eq('type', 'chin')
          .eq('user_id', userId);

        if (error) {
          console.warn('[ChinGlobalRestore] ❌ DB fetch error:', error.message);
          scheduleRetry(attempt);
          return;
        }

        if (!strategies || (strategies as unknown[]).length === 0) {
          console.log('[ChinGlobalRestore] 📭 No chin strategies found for user');
          restoredRef.current = true; // Nothing to restore, don't try again
          return;
        }

        const rows = strategies as unknown as ChinStrategyRow[];
        console.log(`[ChinGlobalRestore] 📋 Found ${rows.length} chin strategy(s): ${rows.map((r) => r.name).join(', ')}`);

        // Step 2: Check cTrader gateway status
        let gatewayAccounts: Array<{ account_id: string }> = [];
        let gatewayReady = false;
        try {
          const { data: gwData } = await supabase.functions.invoke<{
            connected: boolean;
            accounts: Array<{ account_id: string }>;
          }>('ctrader-gateway', {
            method: 'POST',
            body: { action: 'status' },
          });
          console.log(`[ChinGlobalRestore] 🌐 Gateway response: connected=${gwData?.connected}, accounts=${gwData?.accounts?.length}`);
          if (gwData?.connected && gwData?.accounts && gwData.accounts.length > 0) {
            gatewayAccounts = gwData.accounts;
            gatewayReady = true;
            console.log(`[ChinGlobalRestore] 🌐 Gateway online with ${gatewayAccounts.length} account(s): ${gatewayAccounts.map((a) => a.account_id).join(', ')}`);
          } else {
            console.log('[ChinGlobalRestore] ⏳ Gateway not ready yet (connected=false or no accounts)');
          }
        } catch (gwErr) {
          console.log('[ChinGlobalRestore] ⚡ Gateway unreachable:', (gwErr as Error).message);
        }

        if (!gatewayReady) {
          scheduleRetry(attempt);
          return;
        }

        // Step 3: Look up user email for Telegram alerts
        let userEmail = '';
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', userId)
            .maybeSingle();
          if (profile?.email) {
            userEmail = profile.email;
          }
          console.log(`[ChinGlobalRestore] 📧 User email: ${userEmail || '(not found)'}`);
        } catch {
          // non-fatal
        }

        // Step 4: For each strategy, query per-account sessions and start engines
        let startedCount = 0;

        for (const strat of rows) {
          console.log(`[ChinGlobalRestore] 🔍 Processing strategy "${strat.name}" (id=${strat.id})`);

          const activeAccounts = Array.isArray(strat.active_accounts) ? strat.active_accounts : [];
          if (activeAccounts.length === 0) {
            console.log(`[ChinGlobalRestore] ⏭ Strategy "${strat.name}" has no active accounts, skipping`);
            continue;
          }

          // ── Query ALL sessions (composite + legacy) for this strategy ──
          const accountSessions = await querySessionsForStrategy(strat.id);
          console.log(`[ChinGlobalRestore] 📂 Found ${accountSessions.size} session(s) for strategy "${strat.name}": ${[...accountSessions.keys()].join(', ')}`);

          // ── For each active account, start engine ──
          for (const accountToUse of activeAccounts) {
            // Verify account exists in gateway
            const accountExists = gatewayAccounts.some(
              (a) => String(a.account_id) === String(accountToUse),
            );
            if (!accountExists) {
              console.log(`[ChinGlobalRestore] ⚠️ Account ${accountToUse} not in gateway, skipping`);
              continue;
            }

            // Check if engine is already running for this strategy+account
            const existingEngine = getChinEngine(strat.id, accountToUse);
            if (existingEngine && existingEngine.isRunning) {
              console.log(`[ChinGlobalRestore] ⚡ Engine already running for "${strat.name}" / ${accountToUse}`);
              startedCount++;
              continue;
            }

            // ── Get session data for THIS account ──
            const sessionData = accountSessions.get(accountToUse) || null;

            let savedListId: string | null = null;
            let savedStatusMap: Record<number, string> | null = null;
            let savedSymbolConfigs: Record<number, WorkspaceSymbolConfig> | null = null;

            if (sessionData) {
              const ws = sessionData.workspace as Record<string, unknown> | undefined;
              if (ws) {
                savedListId = (ws.selectedListId as string) || null;
                savedStatusMap = (ws.statusMap as Record<number, string>) || null;
                savedSymbolConfigs = (ws.symbolConfigs as Record<number, WorkspaceSymbolConfig>) || null;
              }
            }

            // ── Effective symbol list: saved workspace > strategy default ──
            const effectiveListId = savedListId || strat.symbol_list_id;

            if (!effectiveListId) {
              console.log(`[ChinGlobalRestore] ⚠️ No symbol_list_id for "${strat.name}" / ${accountToUse}, skipping`);
              continue;
            }

            // Fetch the symbol list
            let symbolList: SymbolListRow | null = null;
            try {
              const { data: listData, error: listErr } = await supabase
                .from('symbol_lists')
                .select('id, name, ctrader_account_id, symbols')
                .eq('id', effectiveListId)
                .maybeSingle();

              if (listErr || !listData) {
                console.warn(`[ChinGlobalRestore] ❌ Symbol list ${effectiveListId} not found`);
                continue;
              }
              symbolList = listData as unknown as SymbolListRow;
            } catch {
              continue;
            }

            if (!symbolList.symbols || symbolList.symbols.length === 0) continue;

            // Build symbol infos
            const symbolInfos = symbolList.symbols.map((s) => ({
              symbolId: s.symbolId,
              symbolName: s.symbolName,
              digits: s.digits,
            }));

            const symbolNames: Record<number, string> = {};
            for (const s of symbolList.symbols) {
              symbolNames[s.symbolId] = s.symbolName;
            }

            // Init + start
            try {
              console.log(`[ChinGlobalRestore] 🏗 Initializing engine for "${strat.name}" / ${accountToUse}`);
              const engine = getOrCreateChinEngine(strat.id, accountToUse);

              const chinParams = strat.chinparams as Record<string, unknown> | null;

              await engine.init(
                accountToUse,
                symbolInfos,
                CHIN_TIMEFRAME,
                chinParams || undefined,
                userEmail || undefined,
              );
              engine.start();
              startedCount++;
              console.log(`[ChinGlobalRestore] ✅ Started Chin bot for "${strat.name}" on account ${accountToUse} (${symbolInfos.length} symbols)`);

              restoredRef.current = true;

              // ─── Restore workspace ───
              if (savedStatusMap) {
                for (const [symIdStr, status] of Object.entries(savedStatusMap)) {
                  engine.setSymbolStatus(Number(symIdStr), status);
                }
              }

              // ─── Restore per-symbol Box thresholds (x) + alert on/off ───
              if (savedSymbolConfigs) {
                for (const [symIdStr, cfg] of Object.entries(savedSymbolConfigs)) {
                  const symId = Number(symIdStr);
                  const parsed = parseFloat(cfg.boxX || '');
                  engine.setBoxPipsX(symId, Number.isFinite(parsed) ? parsed : 10);
                  engine.setBoxAlertEnabled(symId, cfg.boxAlertOn !== false);
                }
              }

              if (savedSymbolConfigs && Object.keys(savedSymbolConfigs).length > 0) {
                const result = applyGlobalWorkspace(
                  strat.id,
                  accountToUse,
                  savedSymbolConfigs,
                  symbolNames,
                );

                if (result.offEnginesStarted > 0 || result.ordersStarted > 0) {
                  console.log(
                    `[ChinGlobalRestore] 📋 Workspace restored for "${strat.name}" / ${accountToUse}: ` +
                    `${result.offEnginesStarted} off engine(s) + ${result.ordersStarted} order loop(s)`
                  );
                }
              }
            } catch (initErr) {
              console.warn(
                `[ChinGlobalRestore] ❌ Init failed for "${strat.name}" / ${accountToUse}:`,
                (initErr as Error).message,
              );
            }
          } // end for each active account
        } // end for each strategy

        console.log(`[ChinGlobalRestore] 🏁 Done: ${startedCount} chin engine(s) started`);

        const hasRunnable = rows.some(
          (s) =>
            Array.isArray(s.active_accounts) &&
            s.active_accounts.length > 0 &&
            !!s.symbol_list_id,
        );

        if (startedCount === 0 && hasRunnable) {
          console.log(`[ChinGlobalRestore] ⚠️ 0 engines started but ${rows.length} runnable strategies exist — retrying`);
          scheduleRetry(attempt);
        } else if (startedCount > 0) {
          console.log(`[ChinGlobalRestore] 🎉 Restore complete! ${startedCount} engine(s) running.`);
        }
      } catch (err) {
        console.error('[ChinGlobalRestore] 💥 Unexpected error:', err);
        scheduleRetry(attempt);
      }
    };

    const scheduleRetry = (currentAttempt: number) => {
      const nextAttempt = currentAttempt + 1;
      if (nextAttempt < MAX_RETRIES) {
        retryCountRef.current = nextAttempt;
        console.log(
          `[ChinGlobalRestore] ⏰ Retry ${nextAttempt}/${MAX_RETRIES - 1} in ${RETRY_DELAYS[currentAttempt] / 1000}s...`,
        );
        scheduleTimer(() => tryRestore(nextAttempt), RETRY_DELAYS[currentAttempt]);
      } else {
        console.warn('[ChinGlobalRestore] ❌ Max retries exhausted — giving up. Will NOT retry until next login.');
        restoredRef.current = false;
        retryCountRef.current = 0;
      }
    };

    // Delay initial attempt slightly so Layout has time to resolve role
    scheduleTimer(() => tryRestore(0), 2000);

    // ─── Cleanup: clear all timers and reset ref ───────────
    return () => {
      console.log('[ChinGlobalRestore] 🧹 Cleanup — clearing timers, resetting restoredRef');
      clearAllTimers();
      restoredRef.current = false;
    };
  }, [userId]);
}